import { useState } from "react";
import { useTranslation } from "react-i18next";
import { AppHeader, Icon } from "../common/ui";
import QuizGame from "./QuizGame";
import StrokeTrainer from "./StrokeTrainer";

interface Props {
  token: string;
  onExit: () => void;
  theme: "dark" | "light";
  onThemeChange: (t: "dark" | "light") => void;
}

type Game = "quiz" | "stroke";

export default function GamesHub({ token, onExit, theme, onThemeChange }: Props) {
  const { t } = useTranslation();
  const [game, setGame] = useState<Game | null>(null);

  if (game === "quiz") return <QuizGame token={token} onExit={() => setGame(null)} theme={theme} onThemeChange={onThemeChange} />;
  if (game === "stroke") return <StrokeTrainer onExit={() => setGame(null)} theme={theme} onThemeChange={onThemeChange} />;

  const cards = [
    {
      key: "quiz" as Game, accent: "amber", icon: "trophy",
      title: t("games.quiz.title"), desc: t("games.quiz.desc"),
      chip: t("games.quiz.chip"),
    },
    {
      key: "stroke" as Game, accent: "violet", icon: "brush",
      title: t("games.stroke.title"), desc: t("games.stroke.desc"),
      chip: t("games.stroke.chip"),
    },
  ];

  return (
    <div className="beeapp__scroll">
      <AppHeader theme={theme} onThemeChange={onThemeChange} onBack={onExit} title={t("games.title")} />
      <div className="wrap content-cap" style={{ paddingTop: 8 }}>
        <p className="t-sm muted" style={{ marginBottom: 18, lineHeight: 1.5 }}>{t("games.intro")}</p>
        <div className="grid-menu stagger">
          {cards.map((c) => (
            <button
              key={c.key}
              onClick={() => setGame(c.key)}
              className="glass glass-lg glass-sheen"
              style={{
                textAlign: "left", cursor: "pointer", padding: 22, color: "inherit",
                display: "flex", flexDirection: "column", gap: 16, minHeight: 180,
                position: "relative", overflow: "hidden", border: "none",
              }}
            >
              <div style={{
                position: "absolute", top: -40, right: -40, width: 170, height: 170, borderRadius: "50%",
                background: c.accent === "violet"
                  ? "radial-gradient(circle, var(--violet-glow), transparent 70%)"
                  : "radial-gradient(circle, var(--accent-glow), transparent 70%)",
                opacity: 0.9, pointerEvents: "none",
              }} />
              <div className="between" style={{ position: "relative" }}>
                <span style={{
                  display: "grid", placeItems: "center", width: 54, height: 54, borderRadius: 16,
                  color: c.accent === "violet" ? "var(--violet-ink)" : "var(--accent-ink)",
                  background: c.accent === "violet"
                    ? "linear-gradient(165deg, var(--violet-fill), var(--violet-fill-2))"
                    : "linear-gradient(165deg, var(--accent-fill), var(--accent-fill-2))",
                  boxShadow: "inset 0 1px 0 rgba(255,255,255,0.5)",
                }}>
                  <Icon name={c.icon} size={28} stroke={1.9} />
                </span>
                <span className="icon-btn" aria-hidden style={{ pointerEvents: "none" }}>
                  <Icon name="arrowRight" size={20} />
                </span>
              </div>
              <div style={{ position: "relative", flex: 1 }}>
                <div className="t-title" style={{ marginBottom: 6 }}>{c.title}</div>
                <div className="t-sm muted" style={{ fontWeight: 600, lineHeight: 1.45 }}>{c.desc}</div>
              </div>
              <div className="wraprow" style={{ position: "relative" }}>
                <span className={`badge ${c.accent === "violet" ? "badge-violet" : "badge-amber"}`}>
                  <Icon name="gamepad" size={12} /> {c.chip}
                </span>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
